import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  TablePagination
} from '@mui/material';
import { Add, Edit, Delete, Search, Key, MarkEmailRead } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { customerService } from '../services/api';
import CustomerFormDialog from '../components/CustomerFormDialog';
import ResendWelcomeEmailDialog from '../components/customers/ResendWelcomeEmailDialog';

const Customers = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [passwordResetDialog, setPasswordResetDialog] = useState({ open: false, customer: null, newPassword: '', confirmPassword: '', error: '' });
  const [resendDialog, setResendDialog] = useState({ open: false, customer: null, loading: false });
  const [page, setPage] = useState(0);          // MUI is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerService.getAll({ search, page: page + 1, limit: rowsPerPage });
      setCustomers(response.docs || []);
      setTotalCount(response.totalDocs || 0);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  // Instant refetch when the page or page size changes
  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage]);

  // Debounce the search; jump back to the first page for a new query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (page === 0) {
        fetchCustomers();
      } else {
        setPage(0); // triggers the effect above
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setFormOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setFormOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingCustomer) {
        // Update existing customer
        await customerService.update(editingCustomer._id, formData);
        setSnackbar({
          open: true,
          message: t('customers.messages.updateSuccess'),
          severity: 'success'
        });
      } else {
        // Create new customer
        const response = await customerService.create(formData);
        const successMessage = formData.sendEmail
          ? `${t('customers.messages.createSuccess')}. ${t('customers.messages.emailSentSuccess')}.`
          : `${t('customers.messages.createSuccess')}. ${t('customers.messages.tempPasswordInfo', { password: response.tempPassword || 'N/A' })}`;
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
      }
      setFormOpen(false);
      fetchCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('customers.messages.saveFailed'),
        severity: 'error'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (customer) => {
    setDeleteDialog({ open: true, customer });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.customer) {
      try {
        await customerService.delete(deleteDialog.customer._id);
        setSnackbar({
          open: true,
          message: t('customers.messages.deleteSuccess'),
          severity: 'success'
        });
        fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
        setSnackbar({
          open: true,
          message: t('customers.messages.deleteFailed'),
          severity: 'error'
        });
      }
    }
    setDeleteDialog({ open: false, customer: null });
  };

  const handleToggleFullAccess = async (customer) => {
    try {
      await customerService.update(customer._id, { hasFullAccess: !customer.hasFullAccess });
      fetchCustomers();
    } catch (error) {
      console.error('Error toggling full access:', error);
      setSnackbar({
        open: true,
        message: t('customers.messages.saveFailed'),
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePasswordResetClick = (customer) => {
    setPasswordResetDialog({ open: true, customer, newPassword: '', confirmPassword: '', error: '' });
  };

  const handlePasswordResetConfirm = async () => {
    const { customer, newPassword, confirmPassword } = passwordResetDialog;
    if (!customer) return;

    if (newPassword.length < 6) {
      setPasswordResetDialog(prev => ({ ...prev, error: t('customers.resetPassword.passwordTooShort') }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordResetDialog(prev => ({ ...prev, error: t('customers.resetPassword.passwordMismatch') }));
      return;
    }

    try {
      const response = await customerService.resetPassword(customer._id, { newPassword });
      const successMessage = response.emailSent === false
        ? t('customers.messages.passwordResetSuccess')
        : `${t('customers.messages.passwordResetSuccess')}. ${t('customers.messages.passwordEmailedSuccess')}.`;
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      setPasswordResetDialog({ open: false, customer: null, newPassword: '', confirmPassword: '', error: '' });
    } catch (error) {
      console.error('Error resetting password:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('customers.messages.passwordResetFailed'),
        severity: 'error'
      });
    }
  };

  const handleResendClick = (customer) => {
    setResendDialog({ open: true, customer, loading: false });
  };

  const handleResendConfirm = async ({ resetPassword }) => {
    const { customer } = resendDialog;
    if (!customer) return;

    setResendDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await customerService.resendWelcomeEmail(customer._id, { resetPassword });
      setSnackbar({
        open: true,
        message: response.emailSent === false
          ? t('customers.messages.resendFailed')
          : t('customers.messages.resendSuccess'),
        severity: response.emailSent === false ? 'error' : 'success'
      });
      setResendDialog({ open: false, customer: null, loading: false });
    } catch (error) {
      console.error('Error resending welcome email:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('customers.messages.resendFailed'),
        severity: 'error'
      });
      setResendDialog(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
            {t('customers.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {t('customers.management')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddCustomer}
          size="large"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          {t('customers.add')}
        </Button>
      </Box>

      {/* Search Section */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('common.search')}
          value={search}
          onChange={handleSearch}
          name="customers-search"
          autoComplete="off"
          inputProps={{ autoComplete: 'off' }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table Section */}
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('customers.name')}</TableCell>
              <TableCell>{t('customers.email')}</TableCell>
              <TableCell>{t('customers.phone')}</TableCell>
              <TableCell>{t('customers.city')}</TableCell>
              <TableCell>{t('customers.status')}</TableCell>
              <TableCell>{t('customers.fullAccess')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {t('common.loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {t('customers.noCustomersFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer._id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {customer.fullName || customer.responsiblePerson}
                      </Typography>
                      {customer.fullName && customer.responsiblePerson && (
                        <Typography variant="caption" color="text.secondary">
                          {t('customers.responsiblePerson')}: {customer.responsiblePerson}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell>{customer.city}</TableCell>
                  <TableCell>
                    <Chip
                      label={customer.isActive ? t('customers.active') : t('customers.inactive')}
                      color={customer.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('customers.fullAccessTooltip')}>
                      <Switch
                        size="small"
                        checked={customer.hasFullAccess || false}
                        onChange={() => handleToggleFullAccess(customer)}
                        color="success"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCustomer(customer)}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.50' }
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handlePasswordResetClick(customer)}
                        sx={{
                          color: 'warning.main',
                          '&:hover': { backgroundColor: 'warning.50' }
                        }}
                      >
                        <Key fontSize="small" />
                      </IconButton>
                      <Tooltip title={t('customers.resendWelcome.tooltip')}>
                        <IconButton
                          size="small"
                          onClick={() => handleResendClick(customer)}
                          sx={{
                            color: 'info.main',
                            '&:hover': { backgroundColor: 'info.50' }
                          }}
                        >
                          <MarkEmailRead fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(customer)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'error.50' }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={t('common.rowsPerPage')}
      />

      {/* Customer Form Dialog */}
      <CustomerFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        customer={editingCustomer}
        loading={formLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, customer: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('customers.confirmDelete')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', fontSize: '1rem' }}>
            {t('customers.deleteConfirmation', {
              name: deleteDialog.customer?.fullName || deleteDialog.customer?.email
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, customer: null })}
            size="large"
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            size="large"
            sx={{ textTransform: 'none', minWidth: 100 }}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Confirmation Dialog */}
      <Dialog
        open={passwordResetDialog.open}
        onClose={() => setPasswordResetDialog({ open: false, customer: null, newPassword: '', confirmPassword: '', error: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('customers.resetPassword.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', fontSize: '1rem', mb: 2 }}>
            {t('customers.resetPassword.confirmation', {
              name: passwordResetDialog.customer?.fullName || passwordResetDialog.customer?.email
            })}
          </DialogContentText>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {t('customers.resetPassword.info')}
          </Typography>
          {/* Hidden decoy fields prevent browser password managers from autofilling
              the visible password fields with saved credentials. */}
          <input type="text" name="username" autoComplete="username" style={{ display: 'none' }} readOnly />
          <input type="password" autoComplete="current-password" style={{ display: 'none' }} readOnly />
          <TextField
            fullWidth
            type="password"
            name="customer-new-password"
            label={t('customers.resetPassword.newPasswordLabel')}
            value={passwordResetDialog.newPassword}
            onChange={(e) => setPasswordResetDialog(prev => ({ ...prev, newPassword: e.target.value, error: '' }))}
            sx={{ mb: 2 }}
            inputProps={{ minLength: 6, autoComplete: 'new-password' }}
          />
          <TextField
            fullWidth
            type="password"
            name="customer-new-password-confirm"
            label={t('customers.resetPassword.confirmPasswordLabel')}
            value={passwordResetDialog.confirmPassword}
            onChange={(e) => setPasswordResetDialog(prev => ({ ...prev, confirmPassword: e.target.value, error: '' }))}
            error={Boolean(passwordResetDialog.error)}
            helperText={passwordResetDialog.error || ' '}
            inputProps={{ autoComplete: 'new-password' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setPasswordResetDialog({ open: false, customer: null, newPassword: '', confirmPassword: '', error: '' })}
            size="large"
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handlePasswordResetConfirm}
            color="warning"
            variant="contained"
            size="large"
            disabled={
              passwordResetDialog.newPassword.length < 6 ||
              passwordResetDialog.newPassword !== passwordResetDialog.confirmPassword
            }
            sx={{ textTransform: 'none', minWidth: 100 }}
          >
            {t('customers.resetPassword.title')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Welcome Email Dialog */}
      <ResendWelcomeEmailDialog
        open={resendDialog.open}
        onClose={() => setResendDialog({ open: false, customer: null, loading: false })}
        onConfirm={handleResendConfirm}
        customer={resendDialog.customer}
        loading={resendDialog.loading}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Customers;